import { useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type {
  FinancialReportFilter,
  FinancialReportResult,
  HealthResult,
  Loan,
} from "@dfp/api-client";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { DateRangePicker, rangeForPreset, type DateRange } from "../components/DateRangePicker.js";
import { TodayPanel } from "../components/TodayPanel.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { Badge } from "../components/Badge.js";
import { formatCompactMoney, formatMoney } from "./ReportsPage.js";

export function DashboardPage(): JSX.Element {
  const { user } = useAuth();
  const canReadReports = user?.permissions.includes("report.read") ?? false;
  const health = useQuery({
    queryKey: ["health", "ready"],
    // Routed under /api (issue 9) so the reachability widget works wherever the
    // rest of the admin API is reachable — a deployment whose ingress forwards
    // /api/* but not the infra /health/* probes no longer shows "unreachable".
    queryFn: () => api.get<HealthResult>("/api/system/health/ready", { public: true }),
    refetchInterval: 30_000,
    retry: false,
  });

  const loans = useQuery({
    queryKey: ["loans", "list"],
    queryFn: () => api.get<readonly Loan[]>("/api/loans"),
  });

  // Dashboard period control. Default 30 days; the user can flip
  // presets or pick a custom range from the same picker the Reports
  // page uses. The financial-overview card below re-fetches on every
  // change. Gated on `report.read` — operators without it see the
  // dashboard without the financial summary, no broken state.
  const [range, setRange] = useState<DateRange>(() => rangeForPreset("30d"));
  const filter: FinancialReportFilter = useMemo(
    () => ({ startAt: range.startAt, endAt: range.endAt, bucket: range.bucket }),
    [range],
  );
  const overview = useQuery({
    queryKey: ["reports", "financial", "dashboard", filter],
    queryFn: () => api.post<FinancialReportResult>("/api/reports/financial", filter),
    enabled: canReadReports,
    // v5 — keep the previous response visible across filter changes
    // so the KPIs don't flash to "—". Same `keepPreviousData` helper
    // used on ReportsPage.
    placeholderData: keepPreviousData,
  });

  const byStatus = (loans.data ?? []).reduce<Record<string, number>>((acc, l) => {
    acc[l.status] = (acc[l.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Dynamic greeting widget */}
      <div className="bg-gradient-to-r from-simuflex-navy to-simuflex-deepNavy rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
        {/* Soft decorative glow — a blurred orb off the top-right corner. The
            previous hard-edged 1/3-width band put a visible greenish seam down
            the right edge of the card; a blurred radial has no edge. */}
        <div className="absolute -right-12 -top-16 w-56 h-56 rounded-full bg-simuflex-green/10 blur-3xl pointer-events-none" />
        <h1 className="text-2xl font-extrabold tracking-tight">SimuFlex Operations Hub</h1>
        <p className="text-xs text-slate-300 font-medium mt-1">
          Connected to Tanzania Tenant · Real-time monitoring & device lock management.
        </p>
      </div>

      {/*
        Financial overview — period-scoped KPIs powered by the same
        /api/reports/financial endpoint as the full Reports page.
        Gated on `report.read`; everyone else still sees the rest
        of the dashboard. Tap "Open reports" to drill into the full
        page with filter + trend + breakdowns.
      */}
      {canReadReports ? (
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-simuflex-navy"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 3v18h18M7 14l4-4 4 4 5-5"
                />
              </svg>
              <span>Financial overview · {range.label}</span>
            </CardTitle>
            <Link
              to="/reports"
              className="text-[11px] font-bold uppercase tracking-wider text-simuflex-green hover:text-simuflex-forestGreen"
            >
              Open full report →
            </Link>
          </CardHeader>
          <CardBody className="space-y-4">
            <DateRangePicker value={range} onChange={setRange} />
            {overview.isLoading && !overview.data ? (
              <p className="text-xs text-simuflex-slate">Loading…</p>
            ) : overview.isError ? (
              <p className="text-xs text-red-600">
                Couldn't load the overview — {(overview.error as Error)?.message ?? "unknown"}.
              </p>
            ) : overview.data ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <MiniTile
                  label="Financed"
                  value={formatCompactMoney(overview.data.financedMinor, overview.data.currency)}
                  hint={`${overview.data.loansOpened} loans`}
                />
                <MiniTile
                  label="Collections"
                  value={formatCompactMoney(overview.data.collectionsMinor, overview.data.currency)}
                  hint="in window"
                />
                <MiniTile
                  label="Profit"
                  value={formatCompactMoney(overview.data.profitMinor, overview.data.currency)}
                  hint="realized"
                  accent
                />
                <MiniTile
                  label="Outstanding"
                  value={formatCompactMoney(overview.data.outstandingMinor, overview.data.currency)}
                />
                <MiniTile
                  label="Overdue"
                  value={formatMoney(overview.data.overdueMinor, overview.data.currency)}
                  hint={`${overview.data.arrearsLoanCount} loans`}
                  danger={overview.data.overdueMinor > 0}
                />
                <MiniTile
                  label="Active"
                  value={String(overview.data.activeLoanCount)}
                  hint={`${overview.data.defaultRatePct}% default`}
                />
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      <TodayPanel />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* API Health Widget */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <svg
                className="w-4 h-4 text-simuflex-green"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>API Health</span>
            </CardTitle>
          </CardHeader>
          <CardBody>
            {health.isLoading ? (
              <p className="text-sm text-simuflex-slate">Checking status…</p>
            ) : health.isError ? (
              <div className="flex items-center space-x-2">
                <span className="w-2.5 h-2.5 rounded-full bg-simuflex-orange animate-ping" />
                <span className="text-sm font-bold text-simuflex-orange">API Unreachable</span>
              </div>
            ) : (
              <>
                <div className="flex items-center space-x-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-simuflex-green animate-pulse-glow" />
                  <span className="text-base font-bold text-simuflex-forestGreen">
                    {health.data?.status === "ok"
                      ? "Healthy & Online"
                      : (health.data?.status ?? "Unknown")}
                  </span>
                </div>
                <p className="mt-3 text-xs text-simuflex-slate font-medium leading-relaxed">
                  Dependencies:{" "}
                  {Object.entries(health.data?.info ?? {})
                    .map(([k, v]) => `${k}=${v?.status ?? "?"}`)
                    .join(" · ") || "no info"}
                </p>
              </>
            )}
          </CardBody>
        </Card>

        {/* Loans Loaded Widget */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <svg
                className="w-4 h-4 text-simuflex-navy"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Loans Issued</span>
            </CardTitle>
          </CardHeader>
          <CardBody>
            {loans.isLoading ? (
              <p className="text-sm text-simuflex-slate">Loading active portfolio…</p>
            ) : loans.isError ? (
              <p className="text-sm text-simuflex-orange font-bold">
                Failed to load active contracts
              </p>
            ) : (
              <Link to="/loans" className="group block">
                <p className="text-4xl font-black text-simuflex-navy tracking-tight group-hover:text-simuflex-green">
                  {loans.data?.length ?? 0}
                </p>
                <p className="mt-2 text-xs text-simuflex-slate font-semibold uppercase tracking-wider group-hover:underline">
                  Total Active Loan Records →
                </p>
              </Link>
            )}
          </CardBody>
        </Card>

        {/* By Status Widget */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <svg
                className="w-4 h-4 text-simuflex-green"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2h-2a2 2 0 00-2 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <span>Portfolio Metrics</span>
            </CardTitle>
          </CardHeader>
          <CardBody className="overflow-y-auto max-h-36 scrollbar-thin">
            {Object.entries(byStatus).length === 0 ? (
              <p className="text-sm text-simuflex-slate">No metrics available.</p>
            ) : (
              <ul className="space-y-2">
                {Object.entries(byStatus).map(([status, n]) => (
                  <li key={status}>
                    <Link
                      to="/loans"
                      search={{ status }}
                      className="flex items-center justify-between text-xs rounded px-1 py-0.5 -mx-1 hover:bg-simuflex-mist"
                    >
                      <span className="text-simuflex-navy/80 font-bold tracking-wide uppercase hover:underline">
                        {status}
                      </span>
                      <Badge
                        tone={
                          status === "ACTIVE" || status === "PAID_OFF"
                            ? "success"
                            : status === "LOCKED" || status === "WRITTEN_OFF"
                              ? "danger"
                              : status === "REJECTED"
                                ? "neutral"
                                : "warning"
                        }
                      >
                        {n}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      {/*
        The "Regulatory & Architectural Guidance" card lived here as a
        bootstrap-time crib sheet (single-tenant ADR-0010 / in-platform
        IAM ADR-0011 / aggregate-page convention). Once the platform
        shipped past day one, operators told us it was wall-text noise
        on every dashboard load — removed. The same content lives in
        `docs/adr/` for engineers who actually need it.
      */}
    </div>
  );
}

/**
 * Slim KPI tile used by the dashboard's Financial overview card.
 * Mirrors the larger KpiTile on ReportsPage but tighter so six fit
 * across at desktop width.
 */
function MiniTile({
  label,
  value,
  hint,
  accent = false,
  danger = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  readonly accent?: boolean;
  readonly danger?: boolean;
}): JSX.Element {
  return (
    <div
      className={`rounded-lg border bg-white p-2.5 ${
        danger
          ? "border-red-200"
          : accent
            ? "border-simuflex-green/50 bg-simuflex-green/5"
            : "border-simuflex-line"
      }`}
    >
      <p className="text-[9px] font-bold uppercase tracking-wider text-simuflex-slate">{label}</p>
      <p
        className={`mt-0.5 text-lg font-extrabold ${
          danger ? "text-red-700" : "text-simuflex-navy"
        }`}
      >
        {value}
      </p>
      {hint ? <p className="text-[10px] text-simuflex-slate">{hint}</p> : null}
    </div>
  );
}
